import React from 'react';

/* ===========================================================================
   PHLD.ai — core primitives. Styled only via the CSS tokens in phld.css.
   Ported verbatim from the PHLD.ai Design System (components/core).
   =========================================================================== */

/* ---- Eyebrow ---- */
export function Eyebrow({
  children,
  onInk = false,
  as: Tag = 'div',
  ...rest
}: {
  children: React.ReactNode;
  onInk?: boolean;
  as?: any;
  [key: string]: any;
}) {
  return (
    <Tag
      style={{
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--text-overline)',
        fontWeight: 'var(--weight-medium)',
        letterSpacing: 'var(--tracking-overline)',
        textTransform: 'uppercase',
        color: onInk ? 'var(--text-on-ink-muted)' : 'var(--text-muted)',
        margin: 0,
      }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

/* ---- Button ---- */
export function Button({
  children,
  variant = 'primary',
  size = 'md',
  href,
  icon,
  disabled = false,
  onClick,
  type = 'button',
  ...rest
}: {
  children: React.ReactNode;
  variant?: 'primary' | 'ghost' | 'link';
  size?: 'sm' | 'md' | 'lg';
  href?: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  [key: string]: any;
}) {
  const pad = size === 'lg' ? '15px 30px' : size === 'sm' ? '8px 16px' : '12px 24px';
  const fontSize =
    size === 'lg' ? 'var(--text-body-l)' : size === 'sm' ? 'var(--text-small)' : 'var(--text-body)';

  const base: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    fontFamily: 'var(--font-sans)',
    fontWeight: 'var(--weight-medium)' as any,
    fontSize,
    lineHeight: 1,
    letterSpacing: '0.01em',
    padding: variant === 'link' ? 0 : pad,
    borderRadius: 'var(--radius-pill)',
    border: '1px solid transparent',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.45 : 1,
    textDecoration: 'none',
    transition:
      'background var(--dur-base) var(--ease-out), color var(--dur-base) var(--ease-out), border-color var(--dur-base) var(--ease-out), transform var(--dur-fast) var(--ease-out)',
    whiteSpace: 'nowrap',
  };

  const variants: Record<string, React.CSSProperties> = {
    primary: { background: 'var(--navy)', color: 'var(--blush)' },
    ghost: { background: 'transparent', color: 'var(--ink-900)', borderColor: 'var(--border-strong)' },
    link: {
      background: 'transparent',
      color: 'var(--ink-900)',
      borderRadius: 0,
      borderBottom: '1px solid var(--ink-400)',
      paddingBottom: '3px',
    },
  };

  const style = { ...base, ...variants[variant] };

  const handleEnter = (e: any) => {
    if (disabled) return;
    if (variant === 'primary') e.currentTarget.style.background = 'var(--navy-deep)';
    if (variant === 'ghost') e.currentTarget.style.background = 'var(--surface-raised)';
    if (variant === 'link') e.currentTarget.style.borderBottomColor = 'var(--ink-900)';
  };
  const handleLeave = (e: any) => {
    if (disabled) return;
    if (variant === 'primary') e.currentTarget.style.background = 'var(--navy)';
    if (variant === 'ghost') e.currentTarget.style.background = 'transparent';
    if (variant === 'link') e.currentTarget.style.borderBottomColor = 'var(--ink-400)';
  };
  const handleDown = (e: any) => {
    if (!disabled && variant !== 'link') e.currentTarget.style.transform = 'scale(0.985)';
  };
  const handleUp = (e: any) => {
    e.currentTarget.style.transform = 'none';
  };

  const content = (
    <>
      {children}
      {icon ? (
        <span style={{ display: 'inline-flex', width: '1.1em', height: '1.1em' }} aria-hidden="true">
          {icon}
        </span>
      ) : null}
    </>
  );

  const props: any = {
    style,
    onMouseEnter: handleEnter,
    onMouseLeave: handleLeave,
    onMouseDown: handleDown,
    onMouseUp: handleUp,
    ...rest,
  };

  if (href && !disabled) {
    return (
      <a href={href} {...props}>
        {content}
      </a>
    );
  }
  return (
    <button type={type} disabled={disabled} onClick={onClick} {...props}>
      {content}
    </button>
  );
}

/* ---- Badge ---- */
export function Badge({
  children,
  tone = 'neutral',
  icon,
  ...rest
}: {
  children: React.ReactNode;
  tone?: 'neutral' | 'periwinkle' | 'sky' | 'mint' | 'ink';
  icon?: React.ReactNode;
  [key: string]: any;
}) {
  const tones: Record<string, React.CSSProperties> = {
    neutral: { background: 'var(--surface-raised)', color: 'var(--ink-700)', border: '1px solid var(--border)' },
    periwinkle: { background: 'var(--periwinkle)', color: 'var(--navy)', border: '1px solid transparent' },
    sky: { background: 'var(--sky)', color: 'var(--navy)', border: '1px solid transparent' },
    mint: { background: 'var(--mint)', color: 'var(--mint-deep)', border: '1px solid transparent' },
    ink: { background: 'var(--navy)', color: 'var(--blush)', border: '1px solid transparent' },
  };
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--text-caption)',
        fontWeight: 'var(--weight-medium)' as any,
        letterSpacing: '0.02em',
        padding: '5px 12px',
        borderRadius: 'var(--radius-pill)',
        ...tones[tone],
      }}
      {...rest}
    >
      {icon ? (
        <span style={{ display: 'inline-flex', width: '0.95em', height: '0.95em' }} aria-hidden="true">
          {icon}
        </span>
      ) : null}
      {children}
    </span>
  );
}

/* ---- Card ---- */
export function Card({
  children,
  variant = 'surface',
  padding = 'lg',
  interactive = false,
  style = {},
  ...rest
}: {
  children: React.ReactNode;
  variant?: 'surface' | 'raised' | 'ink';
  padding?: 'sm' | 'md' | 'lg';
  interactive?: boolean;
  style?: React.CSSProperties;
  [key: string]: any;
}) {
  const pads: Record<string, string> = { sm: 'var(--space-5)', md: 'var(--space-6)', lg: 'var(--space-7)' };
  const variants: Record<string, React.CSSProperties> = {
    surface: { background: 'var(--surface-card)', color: 'var(--text-body)', border: '1px solid var(--border)' },
    raised: { background: 'var(--surface-raised)', color: 'var(--text-body)', border: '1px solid var(--border)' },
    ink: { background: 'var(--surface-ink)', color: 'var(--text-on-ink-muted)', border: '1px solid var(--border-on-ink)' },
  };
  const handleEnter = (e: any) => {
    if (!interactive) return;
    e.currentTarget.style.boxShadow = 'var(--shadow-md)';
    e.currentTarget.style.transform = 'translateY(-2px)';
  };
  const handleLeave = (e: any) => {
    if (!interactive) return;
    e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
    e.currentTarget.style.transform = 'none';
  };
  return (
    <div
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      style={{
        borderRadius: 'var(--radius-lg)',
        padding: pads[padding],
        boxShadow: 'var(--shadow-sm)',
        transition: 'box-shadow var(--dur-base) var(--ease-out), transform var(--dur-base) var(--ease-out)',
        ...variants[variant],
        ...style,
      }}
      {...rest}
    >
      {children}
    </div>
  );
}

/* ---- Field ---- */
export function Field({
  label,
  hint,
  type = 'text',
  textarea = false,
  id,
  rows = 4,
  ...rest
}: {
  label?: string;
  hint?: string;
  type?: string;
  textarea?: boolean;
  id?: string;
  rows?: number;
  [key: string]: any;
}) {
  const fieldId = id || (label ? label.toLowerCase().replace(/\s+/g, '-') : undefined);
  const controlStyle: React.CSSProperties = {
    width: '100%',
    fontFamily: 'var(--font-sans)',
    fontSize: 'var(--text-body)',
    color: 'var(--ink-900)',
    background: 'var(--surface-card)',
    border: '1px solid var(--border-strong)',
    borderRadius: 'var(--radius-sm)',
    padding: '12px 14px',
    lineHeight: 'var(--leading-normal)',
    outline: 'none',
    transition: 'border-color var(--dur-base) var(--ease-out), box-shadow var(--dur-base) var(--ease-out)',
    boxSizing: 'border-box',
    resize: textarea ? 'vertical' : undefined,
  };
  const onFocus = (e: any) => {
    e.currentTarget.style.borderColor = 'var(--periwinkle-deep)';
    e.currentTarget.style.boxShadow = 'var(--shadow-ring)';
  };
  const onBlur = (e: any) => {
    e.currentTarget.style.borderColor = 'var(--border-strong)';
    e.currentTarget.style.boxShadow = 'none';
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
      {label ? (
        <label htmlFor={fieldId} style={{ fontSize: 'var(--text-small)', fontWeight: 'var(--weight-medium)' as any, color: 'var(--ink-700)' }}>
          {label}
        </label>
      ) : null}
      {textarea ? (
        <textarea id={fieldId} rows={rows} style={controlStyle} onFocus={onFocus} onBlur={onBlur} {...rest} />
      ) : (
        <input id={fieldId} type={type} style={controlStyle} onFocus={onFocus} onBlur={onBlur} {...rest} />
      )}
      {hint ? <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-faint)' }}>{hint}</span> : null}
    </div>
  );
}

/* ---- SectionHeading ---- */
export function SectionHeading({
  eyebrow,
  title,
  standfirst,
  onInk = false,
  align = 'left',
  size = 'm',
  maxWidth = '20ch',
}: {
  eyebrow?: React.ReactNode;
  title: React.ReactNode;
  standfirst?: React.ReactNode;
  onInk?: boolean;
  align?: 'left' | 'center';
  size?: 's' | 'm' | 'l';
  maxWidth?: string;
}) {
  const sizes: Record<string, string> = {
    m: 'var(--text-display-m)',
    l: 'var(--text-display-l)',
    s: 'var(--text-heading)',
  };
  return (
    <header style={{ textAlign: align, ...(align === 'center' ? { marginInline: 'auto' } : {}) }}>
      {eyebrow ? <Eyebrow onInk={onInk}>{eyebrow}</Eyebrow> : null}
      <h2
        style={{
          fontFamily: 'var(--font-display)',
          fontWeight: 'var(--weight-light)' as any,
          fontSize: sizes[size],
          lineHeight: 'var(--leading-snug)',
          letterSpacing: 'var(--tracking-display)',
          color: onInk ? 'var(--text-on-ink)' : 'var(--text-strong)',
          margin: eyebrow ? 'var(--space-4) 0 0' : 0,
          maxWidth,
          ...(align === 'center' ? { marginInline: 'auto' } : {}),
        }}
      >
        {title}
      </h2>
      {standfirst ? (
        <p
          style={{
            fontSize: 'var(--text-subtitle)',
            fontWeight: 'var(--weight-regular)' as any,
            lineHeight: 'var(--leading-normal)',
            color: onInk ? 'var(--text-on-ink-muted)' : 'var(--text-muted)',
            margin: 'var(--space-5) 0 0',
            maxWidth: '46ch',
            ...(align === 'center' ? { marginInline: 'auto' } : {}),
          }}
        >
          {standfirst}
        </p>
      ) : null}
    </header>
  );
}
